import BackButtonToolbar from "features/BackButtonToolbar";
import React, { useCallback, useEffect, useState } from "react";
import { ResourceContextProvider, SimpleForm, Title, useNotify, useRedirect, useRefresh } from "react-admin";
import { useLocation, useParams } from "react-router";
import { CURRICULUMS, StudentServicePath, TOPICS } from "utils/constants";
import { Box, Card, FormControl, Grid, InputLabel, MenuItem, Select } from "@mui/material";
import EditForm from "./EditForm";
import dataProvider from "server/dataProvider";
import { studentRequests } from "api/studentApi";
import { useNotifyError } from "utils/useNotifyError";

const EditTopics = () => {
  const notifyError = useNotifyError();
  const notify = useNotify();
  const refresh = useRefresh();
  const redirect = useRedirect();
  const location = useLocation();
  const disciplineName = location.state.disciplineName;
  const disciplineId = location.state.disciplineId;
  const academicYearId = location.state.academicYearId;
  const { id } = useParams();

  const [semesters, setSemesters] = useState(null);
  const [defaultValues, setDefaultValues] = useState(null);
  const [modules, setModules] = useState(null);
  const [selectedModuleId, setSelectedModuleId] = useState("");

  const hasModules = modules?.length > 0;

  const fetchSemesters = useCallback(async () => {
    try {
      const { data } = await dataProvider.customRequest(
        studentRequests.getCurriculumDisciplineSemesters(id, disciplineId),
        null,
        "GET",
      );
      setSemesters(data);
    } catch (err) {
      console.error(err);
      notify("Неуспешно зареждане на семестрите. Опитайте да презаредите страницата.", { type: "error" });
    }
  }, [disciplineId, id, notify]);

  const fetchModules = useCallback(async () => {
    try {
      const { data } = await dataProvider.customRequest(
        studentRequests.getCurriculumDisciplineModulesSelect(id, disciplineId),
        null,
        "GET",
      );
      setModules(data);
    } catch (err) {
      console.error(err);
      notify("Неуспешно зареждане на модулите. Опитайте да презаредите страницата.", { type: "error" });
    }
  }, [disciplineId, id, notify]);

  useEffect(() => {
    if (disciplineId) {
      fetchSemesters();
      fetchModules();
    }
  }, [fetchModules, fetchSemesters, disciplineId]);

  const fetchDefaultValues = useCallback(
    async moduleId => {
      setDefaultValues(null);
      try {
        const { data } = await dataProvider.customRequest(
          studentRequests.getCurriculumDisciplineTopics(id, disciplineId, academicYearId, { moduleId }),
          null,
          "GET",
        );

        const groupedBySemester = data?.reduce((acc, topic) => {
          const key = `topic_semester_${topic.semester}`;
          if (!acc[key]) {
            acc[key] = [];
          }
          acc[key].push(topic);
          return acc;
        }, {});

        setDefaultValues(groupedBySemester);
      } catch (err) {
        console.error(err);
        notify("Неуспешно зареждане на темите. Опитайте да презаредите страницата.", { type: "error" });
      }
    },
    [academicYearId, disciplineId, id, notify],
  );

  useEffect(() => {
    if (!id || !disciplineId || !academicYearId || modules === null) return;
    // Modular discipline: wait for a module to be picked, then load topics per module.
    if (hasModules && !selectedModuleId) return;
    fetchDefaultValues(hasModules ? selectedModuleId : undefined);
  }, [id, disciplineId, academicYearId, modules, selectedModuleId, fetchDefaultValues, hasModules]);

  const handleSubmit = async data => {
    try {
      const allTopics = Object.keys(data)
        .filter(key => key.startsWith("topic_semester_"))
        .reduce((acc, key) => {
          const semesterFromKey = parseInt(key.replace("topic_semester_", ""));
          const semesterTopics = data[key] || [];

          const validTopics = semesterTopics
            .filter(topic => topic !== null && topic !== undefined)
            .map(topic => ({
              ...topic,
              semester: topic.semester || semesterFromKey,
              weekNumber: topic.weekNumber,
              isColloquium: !!topic.isColloquium,
            }));
          return acc.concat(validTopics);
        }, []);
      const payload = {
        ...(hasModules ? { moduleId: selectedModuleId } : {}),
        topics: allTopics,
      };
      await dataProvider.customRequest(
        studentRequests.updateCurriculumDisciplineTopics(id, disciplineId, academicYearId),
        payload,
        "PUT",
      );
      notify("Темите са записани успешно", { type: "success" });
      refresh();
      redirect(-1);
    } catch (err) {
      notifyError(err);
    }
  };

  return (
    <>
      <Title title={`Редактиране на тема за дисциплина ${disciplineName}`} />
      <BackButtonToolbar />
      <ResourceContextProvider value={`${StudentServicePath}${TOPICS}/${CURRICULUMS}/${id}/edit`}>
        {hasModules && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
            <Card sx={{ p: 3, width: "500px" }}>
              <Grid container>
                <Grid
                  item
                  xs={12}
                >
                  <FormControl fullWidth>
                    <InputLabel>Модул</InputLabel>
                    <Select
                      label="Модул"
                      value={selectedModuleId}
                      onChange={e => setSelectedModuleId(e.target.value)}
                    >
                      {modules.map(module => (
                        <MenuItem
                          key={module.id}
                          value={module.id}
                        >
                          {module.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Card>
          </Box>
        )}
        {defaultValues && semesters && (!hasModules || selectedModuleId) && (
          <SimpleForm
            key={selectedModuleId}
            defaultValues={defaultValues}
            onSubmit={handleSubmit}
          >
            <EditForm
              semesters={semesters}
              disciplineId={disciplineId}
            />
          </SimpleForm>
        )}
      </ResourceContextProvider>
    </>
  );
};

export default EditTopics;
